import { ChevronDown, ChevronUp, Pencil, Sprout, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Control,
  UseFormHandleSubmit,
  useFormContext,
  useFormState,
  useWatch
} from "react-hook-form";
import { CustomButton } from "../../../../components/ui/button/CustomButton/CustomButton";
import { Badge } from "../../../../components/ui/common/badge";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { validateCharacterLength } from "../../../../utils/utils";

interface FarmGateDetailsProps {
  control: Control<any>;
  farmGateId?: string;
  lockStatus?: boolean;
  isValid: boolean;
  isSaveEnabled: boolean;
  handleSubmit: UseFormHandleSubmit<any>;
  handleFormSubmit: (data: any) => void;
  setVisibleDeleteModal?: (visible: boolean) => void;
}

const FarmGateDetails = ({
  control,
  farmGateId,
  lockStatus = false,
  isValid,
  isSaveEnabled,
  handleSubmit,
  handleFormSubmit,
  setVisibleDeleteModal
}: FarmGateDetailsProps): JSX.Element => {
  const farmGateName = useWatch({ control, name: "farmGateName" });
  const { errors } = useFormState({ control, name: "farmGateName" });
  const { resetField } = useFormContext();
  const [isOpen, setIsOpen] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const hasFarmGateNameError = !!errors.farmGateName;

  // Collapsing the card while the required name field is invalid hides the error from view
  const closeCard = () => {
    if (hasFarmGateNameError) return;
    setIsOpen(false);
  };

  // Only react to farmGateId — watching farmGateName closed the card on every keystroke while editing
  useEffect(() => {
    if (farmGateId) {
      setIsSaved(true);
      setIsEditing(false);
      setIsOpen(false);
    } else {
      setIsSaved(false);
      setIsEditing(true);
      setIsOpen(true);
    }
  }, [farmGateId]);

  const readOnly = isSaved && !isEditing;

  const handleEdit = () => {
    if (lockStatus) return;
    setIsEditing(true);
    setIsOpen(true);
  };

  const handleSaveConfiguration = async (formData: any): Promise<void> => {
    await handleFormSubmit(formData);
    setIsSaved(true);
    setIsEditing(false);
    setIsOpen(false);
  };

  const getSummaryText = (): string => {
    if (!isSaved || !farmGateName) {
      return "Complete all required fields (*) to save cultivation details";
    }
    return farmGateName;
  };

  return (
    <Card className="tw-border-border !tw-p-0">
      <CardHeader
        className="tw-bg-primary/10 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
        onClick={() => (isOpen ? closeCard() : setIsOpen(true))}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <Sprout className="tw-h-4 tw-w-4 tw-text-primary" />
          <div className="tw-flex-1">
            <p className="tw-text-sm tw-font-semibold tw-text-foreground">
              Farm Gate Configuration
            </p>
            <p className="tw-text-sm tw-text-muted-foreground tw-mt-0.5">{getSummaryText()}</p>
          </div>
          {isSaved && (
            <Badge
              variant="outline"
              className="tw-bg-success/10 tw-text-success tw-border-success/30 tw-text-xs"
            >
              Saved
            </Badge>
          )}
          {isSaved && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={e => {
                e.stopPropagation();
                handleEdit();
              }}
              disabled={lockStatus}
              className="tw-gap-1 tw-text-xs tw-h-7"
            >
              <Pencil className="tw-h-3 tw-w-3" /> Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              setVisibleDeleteModal?.(true);
            }}
            disabled={lockStatus}
            className="tw-gap-1 tw-text-xs tw-h-7 tw-text-destructive hover:tw-bg-destructive/10 hover:tw-text-destructive"
          >
            <Trash2 className="tw-h-3 tw-w-3" /> Delete
          </Button>
          {isOpen ? (
            <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
          ) : (
            <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
          )}
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent
          className={`tw-px-3.5 tw-pt-3.5 tw-pb-3.5 ${
            readOnly || lockStatus ? "tw-opacity-85 tw-pointer-events-none" : ""
          }`}
        >
          <div className="tw-space-y-4">
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <FormInput
                control={control}
                name="farmGateName"
                label="Farm Gate Name"
                placeholder="Farm Gate Name"
                required
                errorMessage="Farm Gate Name is required"
                rules={{
                  validate: (value: string) =>
                    validateCharacterLength(value, 50, "Farm Gate Name cannot exceed 50 characters")
                }}
              />
            </div>
            {!readOnly && (
              <div className="tw-flex tw-items-center tw-justify-between tw-pt-3.5 tw-mt-3.5 tw-border-t tw-border-border">
                {!isValid && (
                  <p className="tw-text-xs tw-text-muted-foreground">
                    Fill all required fields (*) to save
                  </p>
                )}
                <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                  {isEditing && isSaved && (
                    <CustomButton
                      onClick={() => {
                        resetField("farmGateName");
                        setIsEditing(false);
                        setIsOpen(false);
                      }}
                      iconSize="small"
                      className="tw-gap-1.5 tw-py-2.5 !tw-bg-transparent tw-border tw-border-border tw-text-foreground hover:tw-bg-muted/50"
                      title="Cancel"
                      theme="lightgrey"
                    />
                  )}
                  <CustomButton
                    onClick={handleSubmit(handleSaveConfiguration)}
                    disabled={!isSaveEnabled}
                    iconSize="small"
                    className="tw-gap-1.5 tw-py-2.5"
                    title={
                      farmGateId
                        ? isEditing
                          ? "Update Farm Gate Name"
                          : "Save Configuration"
                        : "Save Configuration"
                    }
                    theme="green"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default FarmGateDetails;
